// proposal-dangling-dependency-check.ts
//
// Orchestrator recovery (3.40.0, D3.2): the batch a RECOVERY decision ticket
// proposes must not leave work hanging. Only batches from a ticket the
// controller wrote as a recovery ticket (controllerAuthoredDecisionTicket —
// judged on the ticket as it was BEFORE this batch, so a worker cannot amend
// its own ticket out of scope) are checked; planner/redirect batches never are.
//   (a) every recovery batch: no `todo|blocked` ticket may still depend on a
//       ticket this batch supersedes (only `done` satisfies `depends_on`, so it
//       would wait forever). `cancel` is not judged — it blocks dependents
//       explicitly.
//   (b) + (c) only when the ledger trigger is `counted_cap|timeout_split` AND
//       the batch supersedes that source ticket (a T3 `plan_blocked` batch may
//       create parallel slices):
//   (b) every chain head (created ticket depending on no other created one)
//       inherits each live `depends_on` of the source, and no created ticket
//       depends on a retired ticket;
//   (c) every created ticket leads to ONE final ticket (is an ancestor of it,
//       or is it — the playbook asks for a chain; converging fragments also
//       pass, nothing is left hanging): the one
//       every live downstream dependent of the source now points to (no other
//       created ticket), or — without downstream — the unique created ticket
//       nobody created depends on; every created ticket is an ancestor of (or
//       is) that final ticket.
import {
  type ControllerLedgerState,
  controllerRecoveryOrigin,
} from "./controller-redirect-decision-ticket.js";
import { RETIRED_TICKET_STATUSES } from "./g2-plan-readiness.js";
import type { ProposalRejectionCode } from "./proposal-change-types.js";
import type { RecoveryTicketTrigger } from "./recovery-ticket-shape.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { ancestorsOf } from "./ticket-dag-reachability.js";

const REPLACEMENT_TRIGGERS: ReadonlySet<RecoveryTicketTrigger> = new Set<RecoveryTicketTrigger>([
  "counted_cap",
  "timeout_split",
]);
const WAITING_STATUSES: ReadonlySet<string> = new Set(["todo", "blocked"]);

export interface RecoveryBatchCheckInput {
  /** The ticket whose done attempt this is, as loaded BEFORE the batch. */
  sourceTicket: Ticket | undefined;
  state: ControllerLedgerState;
  /** Tickets before the batch. */
  before: readonly Ticket[];
  /** Tickets after every change + remap of this pass. */
  after: readonly Ticket[];
  /** Status after every change (synthetic supersedes included). */
  statusAfter: Readonly<Record<string, string>>;
  /** Ids created by THIS pass. */
  createdIds: readonly string[];
  /** Existing tickets this pass supersedes (worker + synthetic). */
  supersededIds: readonly string[];
}

export interface RecoveryBatchRejection {
  reason: string;
  code: ProposalRejectionCode;
}

/** Scope of the recovery rules for this batch, or undefined (not a recovery batch). */
export interface RecoveryBatchScope {
  sourceTicketId: string;
  /** Trigger ∈ counted_cap|timeout_split AND the batch supersedes the source: rules (b)/(c) + D3.4. */
  replacesSource: boolean;
}

export function recoveryBatchScope(input: RecoveryBatchCheckInput): RecoveryBatchScope | undefined {
  if (input.sourceTicket === undefined) return undefined;
  const origin = controllerRecoveryOrigin(input.sourceTicket, input.state);
  if (origin === undefined) return undefined;
  const { sourceTicketId, trigger } = origin;
  const replacesSource =
    REPLACEMENT_TRIGGERS.has(trigger) && input.supersededIds.includes(sourceTicketId);
  return { sourceTicketId, replacesSource };
}

const reject = (reason: string): RecoveryBatchRejection => ({
  reason: `dangling_dependency: ${reason}`,
  code: "dangling_dependency",
});

const status = (input: RecoveryBatchCheckInput, id: string) => input.statusAfter[id] ?? "todo";

function hangingDependent(input: RecoveryBatchCheckInput): RecoveryBatchRejection | undefined {
  for (const s of input.supersededIds) {
    const hanging = input.after.find(
      (t) => t.depends_on.includes(s) && WAITING_STATUSES.has(status(input, t.id)),
    );
    if (hanging !== undefined) {
      return reject(
        `${hanging.id} (${status(input, hanging.id)}) vẫn depends_on ${s} bị supersede trong lô — amend depends_on sang ticket thay thế cuối`,
      );
    }
  }
  return undefined;
}

function upstreamInherited(
  input: RecoveryBatchCheckInput,
  source: Ticket,
  created: readonly Ticket[],
): RecoveryBatchRejection | undefined {
  const createdIds = new Set(input.createdIds);
  for (const t of created) {
    const retired = t.depends_on.find((d) => RETIRED_TICKET_STATUSES.has(status(input, d)));
    if (retired !== undefined) {
      return reject(`ticket mới ${t.id} depends_on ${retired} đã ${status(input, retired)}`);
    }
  }
  const live = source.depends_on.filter((d) => !RETIRED_TICKET_STATUSES.has(status(input, d)));
  for (const head of created.filter((t) => !t.depends_on.some((d) => createdIds.has(d)))) {
    const missing = live.find((d) => !head.depends_on.includes(d));
    if (missing !== undefined) {
      return reject(
        `ticket thay thế đầu ${head.id} thiếu depends_on ${missing} của ${source.id} (thượng nguồn còn sống phải kế thừa)`,
      );
    }
  }
  return undefined;
}

function finalTicket(
  input: RecoveryBatchCheckInput,
  sourceId: string,
  created: readonly Ticket[],
): { id: string } | RecoveryBatchRejection {
  const createdIds = new Set(input.createdIds);
  const afterById = new Map(input.after.map((t) => [t.id, t]));
  const downstream = input.before.filter(
    (t) =>
      t.depends_on.includes(sourceId) &&
      !createdIds.has(t.id) &&
      WAITING_STATUSES.has(status(input, t.id)),
  );
  if (downstream.length === 0) {
    const leaves = created.filter((c) => !created.some((o) => o.depends_on.includes(c.id)));
    if (leaves.length !== 1) {
      return reject(
        `${sourceId} không có hạ nguồn: lô phải tạo một chuỗi với đúng 1 ticket cuối (đang có ${leaves.length})`,
      );
    }
    return { id: (leaves[0] as Ticket).id };
  }
  let final: string | undefined;
  for (const d of downstream) {
    const targets = (afterById.get(d.id)?.depends_on ?? []).filter((x) => createdIds.has(x));
    const target = targets[0];
    if (targets.length !== 1 || target === undefined || (final !== undefined && target !== final)) {
      return reject(
        `hạ nguồn ${d.id} phải depends_on đúng ticket thay thế cuối của chuỗi (đang trỏ: ${targets.join(", ") || "không ticket mới nào"})`,
      );
    }
    final = target;
  }
  return { id: final as string };
}

/** D3.2: refusal for a recovery batch that leaves a dependency hanging, else undefined. */
export function danglingDependencyRejection(
  input: RecoveryBatchCheckInput,
): RecoveryBatchRejection | undefined {
  const scope = recoveryBatchScope(input);
  if (scope === undefined) return undefined;
  const hanging = hangingDependent(input);
  if (hanging !== undefined || !scope.replacesSource) return hanging;

  const source = input.before.find((t) => t.id === scope.sourceTicketId);
  if (source === undefined) return undefined; // unknown_target already refused a supersede of it
  const createdIds = new Set(input.createdIds);
  const created = input.after.filter((t) => createdIds.has(t.id));
  const inherited = upstreamInherited(input, source, created);
  if (inherited !== undefined) return inherited;

  const final = finalTicket(input, source.id, created);
  if ("reason" in final) return final;
  const byId = new Map(input.after.map((t) => [t.id, t]));
  const chain = ancestorsOf(final.id, byId);
  const stray = created.find((t) => t.id !== final.id && !chain.has(t.id));
  if (stray !== undefined) {
    return reject(
      `ticket mới ${stray.id} không nằm trong chuỗi tới ticket cuối ${final.id} (chia song song không được — nối mảnh k depends_on mảnh k−1)`,
    );
  }
  return undefined;
}
